import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import type { FontContextValue } from '@/providers/font'
import type { GetFamilyFn, GetVariantFn } from '../types/font-lookup'

/**
 * The style update for "apply this font family", **preserving each selected run's own variant**.
 *
 * A `TextRun` carries no bold/italic booleans — the face is encoded *entirely* in `fontId` (see
 * `applyTypographyStyle`). So emitting a single `fontId` for a family change, as all three family
 * controls used to, assigned that one face to every run in range: `mergeTextRunStyle` overwrites
 * `fontId` unconditionally, so every bold or italic run collapsed to the new family's Regular. The
 * runs then became genuinely identical and `mergeAdjacentRuns` legitimately fused them — which is
 * both halves of QA's "changing font family merges/loses formatting of existing text"
 * (WMPCM-9070 Bug 2), from one cause.
 *
 * The remedy is the mechanism the Style select already used for mixed families: `fontIdByFontId`, a
 * per-run *remap* keyed by the run's current `fontId` (`text-style-engine.ts`'s `mergeTextRunStyle`).
 * Each distinct face in the selection is matched to the same-named style in the target family, so
 * bold stays bold and italic stays italic.
 *
 * `fontId` stays in the payload as the fallback, and deliberately so:
 * - a run whose current face has no counterpart in the target family has nowhere to map to;
 * - a run with no `fontId` at all is skipped by the remap (it is guarded on `run.fontId !== undefined`);
 * - `pickRunStyleFields` drops `fontIdByFontId` when capturing a *pending* typing style for a
 *   collapsed caret, so that path needs a concrete face.
 *
 * `mergeTextRunStyle` applies `fontId` first and then lets the remap override per run, which is
 * exactly the precedence this relies on.
 *
 * Re-applying the same family is idempotent: source ids come from the old family and targets from
 * the new one, so a key can only collide with its own value when the two families are the same, in
 * which case it maps to itself.
 */
export function buildFamilyChangeUpdate(
  fontFamilyId: number,
  defaultFontId: number,
  sourceFontIds: readonly number[],
  getVariant: GetVariantFn,
  getFamily: GetFamilyFn
): { update: TextRunStyleUpdate; resolvedVariantFontId: number } {
  const targetFonts = getFamily(fontFamilyId)?.fonts ?? []
  const fontIdByFontId: Record<number, number> = {}

  for (const sourceFontId of sourceFontIds) {
    const styleName = getVariant(sourceFontId)?.fontStyleName

    if (!styleName) continue

    const target = targetFonts.find((f) => f.fontStyle?.fontStyleName === styleName)

    // No same-named style in the target family: leave it unmapped so `fontId` below applies.
    if (target) fontIdByFontId[sourceFontId] = target.fontId
  }

  const mapped = Object.values(fontIdByFontId)
  const hasMap = mapped.length > 0

  return {
    update: {
      fontFamilyId,
      fontId: defaultFontId,
      ...(hasMap ? { fontIdByFontId } : {}),
    },
    // What the Style field should show afterwards when the selection resolves to one face. When the
    // sources map to several distinct faces the caller shows Mixed instead of snapping to Regular.
    resolvedVariantFontId: mapped.length === 1 ? mapped[0]! : defaultFontId,
  }
}

/**
 * The Style select's remap for a mixed-family selection: `styleName` is a style name rather than a
 * face, so each selected face maps to the same-named style within its OWN family.
 */
export function buildStyleNameRemap(
  sourceFontIds: readonly number[],
  styleName: string,
  getVariant: FontContextValue['getVariantByFontId'],
  getFamily: FontContextValue['getFamilyById']
): Record<number, number> {
  const fontIdByFontId: Record<number, number> = {}

  for (const currentFontId of sourceFontIds) {
    const variant = getVariant(currentFontId)

    if (!variant) continue

    const family = getFamily(variant.fontFamilyId)

    if (!family) continue

    const target = (family.fonts ?? []).find((f) => f.fontStyle?.fontStyleName === styleName)

    if (target) fontIdByFontId[currentFontId] = target.fontId
  }

  return fontIdByFontId
}
